import { Autocomplete, Avatar, Box, Button, Grid, IconButton, Link, MenuItem, TextField, Typography } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Send } from "@mui/icons-material";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import GlobalDialogContent from "../GlobalDialogContent";
import { RootState } from "../../../redux/store";
import { closeDialog } from "../../../redux/reducer/dialogSlice";
import { showSnackbar } from "../../../redux/reducer/snackbarSlice";
import { setModuleRefresh } from "../../../redux/reducer/registration/RefreshSlice";
import { CsrUserStoreService, CsrUserUpdateService } from "../../../utils/services/csr-user.service";
import { SewaCategoryListService } from "../../../utils/services/sewa-master.service";
import { REACT_APP_CDN_URL } from "../../../utils/base-urls";

const FORM_ID = "csr-cell-user-form";

export default function AddCsrCellUser() {
    const dispatch = useDispatch();
    const { payload } = useSelector((state: RootState) => state.dialog);
    const isEdit = Boolean(payload?.id);
    const [categoryList, setCategoryList] = useState<any[]>([]);

    useEffect(() => {
        const fetchCategoryList = async () => {
            const { data, code } = await SewaCategoryListService({});
            if (code === 200 && data?.sewa_categories) {
                setCategoryList(data.sewa_categories);
            } else {
                setCategoryList([]);
            }
        };
        fetchCategoryList();
    }, []);

    return (
        <GlobalDialogContent
            dialogBody={
                <Formik
                    enableReinitialize
                    initialValues={{
                        name: payload?.name || "",
                        designation: payload?.designation || "",
                        category_id: payload?.category_id || "",
                        email: payload?.email || "",
                        mobile_no: payload?.mobile_no || payload?.contact || "",
                        address: payload?.address || "",
                        state: payload?.state || "",
                        district: payload?.district || "",
                        pincode: payload?.pincode || "",
                        website: payload?.website || "",
                        profile_photo: payload?.profile_photo || null,
                        department_certificate: payload?.department_certificate || null,
                    }}
                    validationSchema={Yup.object({
                        name: Yup.string().required("Name is Required"),
                        designation: Yup.string().required("Designation is Required"),
                        category_id: Yup.string(),
                        email: Yup.string().email("Invalid email format").required("Email is Required"),
                        mobile_no: Yup.string().matches(/^[6-9]\d{9}$/, "Enter valid 10-digit number").required("Mobile Number is Required"),
                        address: Yup.string().required("Address is Required"),
                        state: Yup.string().required("State is Required"),
                        district: Yup.string().required("District is Required"),
                        pincode: Yup.string().matches(/^\d{6}$/, "Enter valid 6-digit pincode").required("Pincode is Required"),
                        website: Yup.string().url("Enter a valid URL").nullable(),
                    })}
                    onSubmit={async (values, { setSubmitting }) => {
                        const formData = new FormData();
                        formData.append("name", values.name);
                        formData.append("designation", values.designation);
                        formData.append("category_id", values.category_id);
                        formData.append("email", values.email);
                        formData.append("mobile_no", values.mobile_no);
                        formData.append("address", values.address);
                        formData.append("state", values.state);
                        formData.append("district", values.district);
                        formData.append("pincode", values.pincode);
                        formData.append("website", values.website || "");

                        if (values.profile_photo instanceof File) {
                            formData.append("profile_photo", values.profile_photo);
                        }
                        if (values.department_certificate instanceof File) {
                            formData.append("department_certificate", values.department_certificate);
                        }
                        if (isEdit) {
                            formData.append("id", payload.id);
                        }

                        const apiFunction = isEdit ? CsrUserUpdateService : CsrUserStoreService;
                        const { code, message } = await apiFunction(formData);

                        if (code === 200) {
                            dispatch(showSnackbar({ type: "success", message: message || (isEdit ? "CSR Cell User updated successfully" : "CSR Cell User added successfully") }));
                            dispatch(closeDialog());
                            dispatch(setModuleRefresh({ moduleName: "CsrCellRefresh", refresh: true }));
                        } else {
                            dispatch(showSnackbar({ type: "error", message: message || "Something went wrong, please try again later" }));
                        }
                        setSubmitting(false);
                    }}
                >
                    {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => {
                        const existingCertificate = typeof values.department_certificate === "string" ? values.department_certificate : "";
                        return (
                            <Form id={FORM_ID}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} display="flex" flexDirection="column" alignItems="center">
                                        <Avatar
                                            src={
                                                values.profile_photo instanceof File
                                                    ? URL.createObjectURL(values.profile_photo)
                                                    : typeof values.profile_photo === "string" && values.profile_photo
                                                        ? `${REACT_APP_CDN_URL}/${values.profile_photo}`
                                                        : ""
                                            }
                                            sx={{ width: 64, height: 64, mb: 1 }}
                                        />
                                        <Button variant="outlined" component="label" size="small" startIcon={<AddAPhotoIcon />}>
                                            {values.profile_photo ? "Change Photo" : "Upload Photo"}
                                            <input
                                                type="file"
                                                hidden
                                                accept="image/*"
                                                onChange={(e) => setFieldValue("profile_photo", e.target.files?.[0] || null)}
                                            />
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Name *"
                                            name="name"
                                            placeholder="Enter Name"
                                            value={values.name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.name && Boolean(errors.name)}
                                            helperText={touched.name && (errors.name as string)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Designation *"
                                            name="designation"
                                            placeholder="Enter Designation"
                                            value={values.designation}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.designation && Boolean(errors.designation)}
                                            helperText={touched.designation && (errors.designation as string)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Autocomplete
                                            size="small"
                                            options={categoryList}
                                            getOptionLabel={(option: any) => option?.category_name || ""}
                                            isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                                            value={categoryList.find((category: any) => category.id === Number(values.category_id)) || null}
                                            onChange={(_, value: any) => setFieldValue("category_id", value ? value.id : "")}
                                            onBlur={handleBlur}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    name="category_id"
                                                    label="Category "
                                                    placeholder="Select Category"
                                                    error={touched.category_id && Boolean(errors.category_id)}
                                                    helperText={touched.category_id && (errors.category_id as string)}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Email *"
                                            name="email"
                                            type="email"
                                            placeholder="Enter Email"
                                            value={values.email}
                                            onChange={(e) => setFieldValue("email", e.target.value.toLowerCase())}
                                            onBlur={handleBlur}
                                            error={touched.email && Boolean(errors.email)}
                                            helperText={touched.email && (errors.email as string)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Mobile Number *"
                                            name="mobile_no"
                                            inputProps={{ maxLength: 10, inputMode: "numeric" }}
                                            placeholder="Enter Mobile Number"
                                            value={values.mobile_no}
                                            onChange={(e) => {
                                                if (/^\d*$/.test(e.target.value)) setFieldValue("mobile_no", e.target.value);
                                            }}
                                            onBlur={handleBlur}
                                            error={touched.mobile_no && Boolean(errors.mobile_no)}
                                            helperText={touched.mobile_no && (errors.mobile_no as string)}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="State *"
                                            name="state"
                                            placeholder="Enter State"
                                            value={values.state}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.state && Boolean(errors.state)}
                                            helperText={touched.state && (errors.state as string)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="District *"
                                            name="district"
                                            placeholder="Enter District"
                                            value={values.district}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.district && Boolean(errors.district)}
                                            helperText={touched.district && (errors.district as string)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Pincode *"
                                            name="pincode"
                                            inputProps={{ maxLength: 6, inputMode: "numeric" }}
                                            placeholder="Enter Pincode"
                                            value={values.pincode}
                                            onChange={(e) => {
                                                if (/^\d*$/.test(e.target.value)) setFieldValue("pincode", e.target.value);
                                            }}
                                            onBlur={handleBlur}
                                            error={touched.pincode && Boolean(errors.pincode)}
                                            helperText={touched.pincode && (errors.pincode as string)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Website"
                                            name="website"
                                            placeholder="Enter Website URL"
                                            value={values.website}
                                            multiline
                                            minRows={2}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.website && Boolean(errors.website)}
                                            helperText={touched.website && (errors.website as string)}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Address *"
                                            name="address"
                                            multiline
                                            minRows={2}
                                            placeholder="Enter Address"
                                            value={values.address}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.address && Boolean(errors.address)}
                                            helperText={touched.address && (errors.address as string)}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                            Department Certificate
                                        </Typography>
                                        <Button variant="outlined" component="label" startIcon={<AddAPhotoIcon />} size="small">
                                            {values.department_certificate ? "Change File" : "Upload File"}
                                            <input
                                                type="file"
                                                hidden
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                onChange={(e) => setFieldValue("department_certificate", e.target.files?.[0] || null)}
                                            />
                                        </Button>
                                        {values.department_certificate instanceof File && (
                                            <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                                                {values.department_certificate.name}
                                                <IconButton size="small" color="error" onClick={() => setFieldValue("department_certificate", isEdit ? payload?.department_certificate || null : null)}>
                                                    <HighlightOffIcon fontSize="small" />
                                                </IconButton>
                                            </Typography>
                                        )}
                                        {existingCertificate && (
                                            <Link sx={{ ml: 1 }}>
                                                Uploaded Certificate
                                            </Link>
                                        )}
                                    </Grid>
                                </Grid>
                            </Form>
                        );
                    }}
                </Formik>
            }
            dialogFooter={
                <Button type="submit" form={FORM_ID} variant="contained" color="primary" startIcon={<Send />}>
                    {isEdit ? "Update" : "Add"}
                </Button>
            }
        />
    );
}
